import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, GraduationCap, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API, listStrategies, getStrategy } from "../lib/api";
import { markdownComponents } from "./LessonDocument";
import { useAuth } from "../contexts/AuthContext";
import DownloadPdfButton from "./DownloadPdfButton";

const KIND_LABEL = {
  transferable_skill: "Transferable Skill",
  learning_skill: "Learning Skill",
};

const KIND_PILL_CLASS = {
  transferable_skill: "bg-[#96ded1]/30 border-[#96ded1]/60 text-[#1c2d3d]",
  learning_skill: "bg-[#4a6984]/10 border-[#4a6984]/30 text-[#4a6984]",
};

export const StrategyLibrary = () => {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {slug,title,kind,summary,markdown}
  const [loadingActive, setLoadingActive] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await listStrategies();
        if (!cancelled) setStrategies(data.strategies || []);
      } catch (e) {
        if (!cancelled) {
          toast.error("Could not load the skills library");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openStrategy = async (slug) => {
    setLoadingActive(true);
    try {
      const data = await getStrategy(slug);
      setActive(data);
      // Scroll the panel into view at the top of the tab content.
      window.requestAnimationFrame(() => {
        document
          .querySelector('[data-testid="strategy-detail"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (e) {
      toast.error("Could not load that playbook");
    } finally {
      setLoadingActive(false);
    }
  };

  if (loading) {
    return <p className="text-[#4a6984]/70">Loading skills library…</p>;
  }

  if (active) {
    return (
      <div data-testid="strategy-detail" className="ae-card p-6 md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setActive(null)}
            data-testid="strategy-back"
            className="inline-flex items-center gap-1.5 text-[#4a6984] hover:text-[#1c2d3d] font-extrabold uppercase tracking-[0.14em] text-xs"
          >
            <ChevronLeft className="h-4 w-4" /> Back to library
          </button>
          <DownloadPdfButton
            href={`${API}/strategies/${encodeURIComponent(active.slug)}/pdf`}
            testId={`strategy-download-pdf-${active.slug}`}
            label="Download this playbook (PDF)"
          />
        </div>

        <header className="mt-5 mb-6">
          <span
            className={`ae-pill border ${KIND_PILL_CLASS[active.kind] || ""}`}
            data-testid="strategy-kind"
          >
            {KIND_LABEL[active.kind] || active.kind}
          </span>
          <h2
            data-testid="strategy-title"
            className="sr-only"
          >
            {active.title}
          </h2>
        </header>

        <div data-testid="strategy-markdown" className="ae-prose">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {active.markdown}
          </ReactMarkdown>
        </div>

        {/* Skill -> Generator handoff. Replaces the old in-page mini
            generator. Signed-out teachers see "Try for free" leading to
            the Skills Generator; signed-in teachers see "Generate a
            lesson" with the skill pre-filled on the main /generator page. */}
        <div
          data-testid="strategy-cta-row"
          className="mt-12 ae-card border-l-[3px] border-l-[#96ded1] p-6 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
        >
          <div>
            <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
              Bring this skill into tomorrow&apos;s class
            </p>
            <p className="mt-2 text-[#1c2d3d] font-extrabold text-lg leading-snug">
              {user
                ? `Generate a ${active.title} activity →`
                : `Try Atomic Edu free — start with ${active.title}`}
            </p>
          </div>
          <Link
            to={
              user
                ? `/generator?skill=${encodeURIComponent(active.title)}`
                : `/generator?skill=${encodeURIComponent(active.title)}#skills-generator`
            }
            data-testid="strategy-cta-link"
            className="ae-cta h-12 px-6 rounded-lg text-sm font-bold inline-flex items-center gap-2 shrink-0"
          >
            Build a Skill
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="strategy-library">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="strategy-grid">
        {strategies.map((s) => {
          const isLearning = s.kind === "learning_skill";
          const Icon = isLearning ? GraduationCap : Sparkles;
          return (
            <button
              key={s.slug}
              type="button"
              onClick={() => openStrategy(s.slug)}
              disabled={loadingActive}
              data-testid={`strategy-card-${s.slug}`}
              className="ae-card p-6 text-left hover:border-[#96ded1] hover:shadow-lg hover:shadow-[#4a6984]/5 transition-all duration-150 disabled:opacity-60 group"
            >
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`ae-pill border ${KIND_PILL_CLASS[s.kind] || ""}`}
                >
                  {KIND_LABEL[s.kind] || s.kind}
                </span>
                <Icon
                  className={
                    "h-5 w-5 transition-colors " +
                    (isLearning
                      ? "text-[#4a6984]/60 group-hover:text-[#4a6984]"
                      : "text-[#4a6984]/40 group-hover:text-[#96ded1]")
                  }
                />
              </div>
              <h4 className="font-extrabold text-xl text-[#4a6984] mt-4 tracking-tight">
                {s.title}
              </h4>
              <p className="mt-2 text-[#4a6984]/80 font-medium text-sm leading-relaxed">
                {s.summary}
              </p>
              <span className="mt-4 inline-block font-extrabold uppercase tracking-[0.16em] text-[10px] text-[#4a6984]/60 group-hover:text-[#1c2d3d]">
                Open playbook →
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StrategyLibrary;
